import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuGroup,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import ResponsiveControls from "@v2/features/page-renderer/components/navbar/ResponsiveControls";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useInfinitePages } from "@v2/hooks/useInfinitePages";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  FolderSearch,
  Search,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import PageCard from "./PageCard/PageCard";
import PageCardSkeleton from "./PageCard/PageCardSkeleton";

const GRID_CONSTANTS = {
  MIN_CARD_WIDTH: 300,
} as const;

const DEFAULT_ORDER: OrderState = {
  field: "updatedAt",
  direction: "desc",
};

type Field = "name" | "updatedAt" | "createdAt";
type Direction = "asc" | "desc";

type OrderState = {
  field: Field;
  direction: Direction;
};

const sortByOptions = [
  { label: "Alphabetical", value: "name" },
  { label: "Last edited", value: "updatedAt" },
  { label: "Created date", value: "createdAt" },
];

const alphabeticalSortOrderOptions = [
  { label: "A to Z", value: "asc" },
  { label: "Z to A", value: "desc" },
];

const chronologicalSortOrderOptions = [
  { label: "Newest First", value: "desc" },
  { label: "Oldest First", value: "asc" },
];

type SortDropdownProps = {
  order: OrderState;
  onOrderChange: (order: OrderState) => void;
};

const SortDropdown: React.FC<SortDropdownProps> = ({
  order,
  onOrderChange,
}) => {
  const getValueLabel = () => {
    const field = order.field;
    let label = "Sort by";

    if (field === "name") {
      label = "Alphabetical";
    } else if (field === "updatedAt") {
      label = "Last edited";
    } else if (field === "createdAt") {
      label = "Created date";
    }

    return label;
  };

  return (
    <Menu>
      <MenuTrigger UNSAFE_className="min-w-full">
        <Button
          variant="secondary"
          size="base"
          startEnhancer={
            order.direction === "asc" ? <ArrowUp /> : <ArrowDown />
          }
          endEnhancer={<ChevronDown />}
          UNSAFE_className="min-w-full text-sm font-normal bg-sheet"
        >
          {getValueLabel()}
        </Button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuGroup>
          <MenuLabel>Sort By</MenuLabel>
          <MenuRadioGroup
            value={order.field}
            onValueChange={(value) => {
              onOrderChange({
                field: value as Field,
                direction: order.direction,
              });
            }}
          >
            {sortByOptions.map((option) => (
              <MenuRadioItem
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </MenuRadioGroup>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuLabel>Sort Order</MenuLabel>
          <MenuRadioGroup
            value={order.direction}
            onValueChange={(value) => {
              onOrderChange({
                field: order.field,
                direction: value as Direction,
              });
            }}
          >
            {(order.field === "name"
              ? alphabeticalSortOrderOptions
              : chronologicalSortOrderOptions
            ).map((option) => (
              <MenuRadioItem
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </MenuRadioGroup>
        </MenuGroup>
      </MenuContent>
    </Menu>
  );
};

function PagesSection({
  setSelectedTab,
}: {
  setSelectedTab: (tab: "pages" | "templates") => void;
}) {
  const [order, setOrder] = React.useState<OrderState>(DEFAULT_ORDER);
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");

  const projectId = useCurrentProjectId();

  const { pages, isLoading, fetchNextPage, hasNextPage } = useInfinitePages({
    projectId: projectId ?? null,
    search: searchQuery,
    limit: 6,
    orderBy: order,
  });

  const noPages = pages?.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearchResults =
    pages?.length === 0 && !isLoading && searchQuery.length > 0;

  if (noPages) {
    return (
      <div className="pb-12">
        <Empty
          title="No pages created yet"
          size="base"
          description="Start building with chat or by using one of our ready-made templates."
          media={
            <img
              src="/images/empty-pages.svg"
              alt="no pages"
              className="h-16 w-24"
            />
          }
          UNSAFE_className="w-full h-full"
        >
          <Button
            variant="secondary"
            size="base"
            endEnhancer={<ArrowRight />}
            onClick={() => {
              setSelectedTab("templates");
            }}
          >
            Use a Template
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex gap-4 flex-col h-full">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2 sm:flex sm:gap-3 sm:items-center w-full flex-shrink-0">
        <div className="flex min-w-0 w-full">
          <Input
            startEnhancer={<Search size={12} />}
            placeholder="Search"
            size="base"
            className="grow"
            value={searchQueryInputValue}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <ResponsiveControls variant="secondary" />
        <div className="col-span-2 sm:col-span-1 sm:flex-1">
          <SortDropdown order={order} onOrderChange={setOrder} />
        </div>
      </div>
      {emptySearchResults ? (
        <Empty
          title="No pages found"
          description="Try a different search term."
          icon={<FolderSearch />}
          UNSAFE_className="py-[10%]"
        />
      ) : (
        <InfiniteScroll
          dataLength={pages.length}
          next={() => void fetchNextPage()}
          hasMore={hasNextPage}
          loader={
            <div
              className="grid gap-4 mt-4"
              style={{
                gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
              }}
            >
              {Array.from({ length: 3 }).map((_, index) => (
                <PageCardSkeleton key={`loader-${index}`} />
              ))}
            </div>
          }
          scrollableTarget="home-scroll-container"
        >
          <div
            className="grid gap-4 pt-4 pb-6"
            style={{
              gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
            }}
          >
            {isLoading
              ? Array.from({ length: 9 }).map((_, index) => (
                  <PageCardSkeleton key={`initial-${index}`} />
                ))
              : pages.map((file) => <PageCard key={file.id} file={file} />)}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
}

export default PagesSection;
